import React, { useState, useRef, useEffect } from "react";
import styles from "./RejectDelModal.module.css";

const RejectDelModal = ({
  onRejectFunction,
  onCancel,
  afterReject,
  title = "Reject DEL",
  message = "Please provide a reason for rejecting this DEL.",
}) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const modalRef = useRef(null);
  const textareaRef = useRef(null);

  // Close when clicking outside the modal
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        // Only close if user hasn't entered any text
        if (!hasInteracted || rejectionReason.trim() === "") {
          onCancel();
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onCancel, rejectionReason, hasInteracted]);

  // Close on escape key
  useEffect(() => {
    function handleEscapeKey(event) {
      if (event.key === "Escape") {
        onCancel();
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onCancel]);

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Auto-close modal after success message is shown
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        afterReject();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, afterReject]);

  const handleReject = async () => {
    // Reset previous error and success state
    setError(null);
    setSuccess(null);

    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejecting this DEL.");
      return;
    }

    try {
      setIsLoading(true);
      const result = await onRejectFunction(rejectionReason);

      // Check if there was an error in the response
      if (result && result.error) {
        setError(result.error);
        setIsLoading(false);
        return;
      }

      // Success message
      setIsLoading(false);
      setSuccess("DEL has been successfully rejected.");

      // Don't close the modal immediately - let the user see the success message
      // The useEffect will handle closing after delay
    } catch (err) {
      setIsLoading(false);
      setError(
        err.message ||
          "An error occurred while rejecting the DEL. Please try again."
      );
      console.error("Error rejecting DEL:", err);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.rejectDelModal} ref={modalRef}>
        <button
          className={styles.closeButton}
          onClick={onCancel}
          aria-label="Close modal"
          disabled={isLoading}
        >
          ×
        </button>
        <h2>{title}</h2>
        <p>{message}</p>

        {error && <div className={styles.errorMessage}>{error}</div>}
        {success && <div className={styles.successMessage}>{success}</div>}

        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={rejectionReason}
          onChange={(e) => {
            setRejectionReason(e.target.value);
            setError(null); // Clear error when user makes a selection
            setSuccess(null); // Clear success when user makes a selection
            setHasInteracted(true);
          }}
          placeholder="Enter rejection reason..."
          rows={4}
          disabled={isLoading || success}
        />

        <button
          className={styles.clearButton}
          onClick={() => {
            setRejectionReason("");
            setError(null);
            setSuccess(null);
          }}
          disabled={isLoading || success}
        >
          Clear
        </button>

        <div className={styles.buttonGroup}>
          <button
            className={styles.rejectButton}
            onClick={handleReject}
            disabled={isLoading || success}
          >
            {isLoading && <span className={styles.loadingIndicator}></span>}
            Reject DEL
          </button>
          <button
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectDelModal;
